/**
 * Server-safe blog content processor.
 *
 * Replaces the heavy client-side DOMPurify + useEffect DOM manipulation
 * with pure string transforms that run in useMemo (or on the server).
 *
 * Visual styling is handled by styles/blog-content.css — this function
 * only performs structural HTML transforms and basic XSS protection.
 */

function extractYouTubeId(url: string): string | null {
  const fullUrlMatch = url.match(/v=([\w-]+)/);
  const shortUrlMatch = url.match(/youtu\.be\/([\w-]+)/);
  return fullUrlMatch?.[1] ?? shortUrlMatch?.[1] ?? null;
}

export function processBlogContent(html: string): string {
  if (!html) return "";

  let out = html;

  // ── Basic XSS protection ──────────────────────────────────────────
  // Strip <script> blocks
  out = out.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  // Strip inline event handlers (onclick, onerror, etc.)
  out = out.replace(/\s+on\w+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\s+on\w+\s*=\s*'[^']*'/gi, "");
  // Strip javascript: URLs
  out = out.replace(/href\s*=\s*"javascript:[^"]*"/gi, 'href="#"');
  out = out.replace(/href\s*=\s*'javascript:[^']*'/gi, "href='#'");
  out = out.replace(/src\s*=\s*"javascript:[^"]*"/gi, 'src=""');

  // ── YouTube oembed → responsive iframe ────────────────────────────
  out = out.replace(
    /<oembed\s+url="(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=[\w-]+|youtu\.be\/[\w-]+)[^"]*)"\s*>\s*<\/oembed>/gi,
    (_match, url: string) => {
      const videoId = extractYouTubeId(url);
      if (!videoId) return _match;
      return `<div class="blog-yt-wrapper"><iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy"></iframe></div>`;
    },
  );

  // ── Cleanup ───────────────────────────────────────────────────────
  // Remove <br>&nbsp; filler patterns
  out = out.replace(/<br\s*\/?>\s*&nbsp;/g, "");

  // Remove headings and paragraphs that contain only &nbsp; or whitespace
  out = out.replace(/<(h[1-6]|p)\b[^>]*>\s*(?:&nbsp;\s*)+<\/\1>/gi, "");

  // ── Strip inline style attributes from CMS elements ───────────────
  // Headings, figures, images — CSS handles their presentation
  out = out.replace(
    /(<(?:h[1-4]|figure|img)\b)([^>]*?)\s+style="[^"]*"/gi,
    "$1$2",
  );

  // ── Convert <strong> to styled <span> ─────────────────────────────
  out = out.replace(
    /<strong>([\s\S]*?)<\/strong>/g,
    '<span style="font-weight:600;color:#213343">$1</span>',
  );

  // ── Simplify <a><span>text</span></a> → <a>text</a> ──────────────
  out = out.replace(
    /(<a\b[^>]*>)\s*<span[^>]*>([\s\S]*?)<\/span>\s*(<\/a>)/gi,
    "$1$2$3",
  );

  // ── Add lazy loading to images that lack it ───────────────────────
  out = out.replace(
    /<img(?![^>]*\bloading\s*=)([^>]*?)(\/?>)/gi,
    '<img loading="lazy"$1$2',
  );

  return out;
}
